import { useCallback, useEffect, useRef, useState } from 'react';
import { useSimulation } from '../context/SimulationContext';
import { simulationService } from '../services/simulation.service';
import { POLLING_INTERVAL_MS } from '../constants/simulation.constants';

export function useSessionSetup(interviewSessionId: string | null) {
  const { dispatch } = useSimulation();
  const [isPolling, setIsPolling] = useState(false);
  const simSessionIdRef = useRef<string | null>(null);

  // Resume an existing sim session (e.g., page refresh or 409 on create)
  const resumeSession = useCallback(
    async (simSessionId: string) => {
      simSessionIdRef.current = simSessionId;

      dispatch({
        type: 'SESSION_CREATED',
        payload: {
          sessionId: interviewSessionId ?? '',
          simSessionId,
        },
      });

      // Check current status to decide next step
      try {
        const statusResponse =
          await simulationService.getSessionStatus(simSessionId);
        const status = statusResponse.status;

        if (status === 'active') {
          dispatch({ type: 'SET_STATUS', payload: 'active' });
        } else if (status === 'paused') {
          dispatch({ type: 'SET_STATUS', payload: 'paused' });
        } else if (status === 'scenarioReady') {
          dispatch({ type: 'SET_STATUS', payload: 'scenarioReady' });
        } else if (status === 'pending') {
          setIsPolling(true);
        } else if (
          status === 'terminated' ||
          status === 'completed' ||
          status === 'abandoned'
        ) {
          dispatch({ type: 'SET_STATUS', payload: 'completed' });
        } else {
          dispatch({ type: 'SET_STATUS', payload: 'failed' });
        }
      } catch {
        dispatch({ type: 'SET_STATUS', payload: 'failed' });
      }
    },
    [dispatch, interviewSessionId],
  );

  const createSession = useCallback(
    async (interviewSessionId: string, roundId: string) => {
      console.log("Creating session with interviewSessionId:", interviewSessionId, "and roundId:", roundId);
      dispatch({ type: 'SET_STATUS', payload: 'loading' });

      try {
        const response = await simulationService.createSession({
          interviewSessionId,
          roundId,
        });
        console.log("Session creation response:", response);

        const simSessionId = response._id ?? response.sessionId;
        simSessionIdRef.current = simSessionId;

        dispatch({
          type: 'SESSION_CREATED',
          payload: {
            sessionId: interviewSessionId,
            simSessionId,
            blueprintId: response.blueprintId,
          },
        });

        setIsPolling(true);
      } catch (error: any) {
        // If a session already exists, resume it instead of failing
        if (error?.response?.status === 409 && error?.response?.data?.sessionId) {
          await resumeSession(error.response.data.sessionId);
          return;
        }
        dispatch({
          type: 'SET_ERROR',
          payload: error?.response?.data?.message ?? 'Failed to create session',
        });
        dispatch({ type: 'SET_STATUS', payload: 'failed' });
      }
    },
    [dispatch, resumeSession],
  );

  // Poll for scenario readiness
  useEffect(() => {
    if (!isPolling || !simSessionIdRef.current) return;

    const interval = setInterval(async () => {
      try {
        const statusResponse = await simulationService.getSessionStatus(
          simSessionIdRef.current!,
        );
        const status = statusResponse.status;

        if (status === 'scenarioReady' || status === 'active') {
          clearInterval(interval);
          setIsPolling(false);

          if (status === 'scenarioReady') {
            // Start the session (transition scenarioReady → active)
            await simulationService.startSession(simSessionIdRef.current!);
          }

          dispatch({ type: 'SET_STATUS', payload: 'active' });
        }

        if (status === 'failed' || status === 'terminated') {
          clearInterval(interval);
          setIsPolling(false);
          dispatch({
            type: 'SET_ERROR',
            payload: 'Scenario generation failed',
          });
          dispatch({ type: 'SET_STATUS', payload: 'failed' });
        }
      } catch {
        // Continue polling on network errors
      }
    }, POLLING_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [isPolling, dispatch]);

  return { createSession, resumeSession };
}
